import React, { useMemo, useState } from "react";
import {
  MyMessage,
  InputField,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../components/Pricing";
import GenerateDescription from "../utils/GenerateDescription";
import {
  ContactSection,
  SocialSection,
  GeoSection,
  VisibilitySection,
  DescriptionSection,
} from "../utils/AddSections";
import { FiImage, FiClock, FiMap, FiDollarSign } from "react-icons/fi";

const slugify = (s: any) =>
  s
    ? s
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
    : null;

const Add = () => {
  const { createOneContent } = useDoSeeDataContext();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
    tags,
  } = useGeoPositionContext();

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");

  const [title, setTitle] = useState("");
  const [venue, setVenue] = useState("");
  const [venueCapacity, setVenueCapacity] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [email, setEmail] = useState("");
  const [payLink, setPayLink] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  const [whatsapp, setWhatsapp] = useState("");
  const [facebook, setFacebook] = useState("");
  const [twitter, setTwitter] = useState("");
  const [instagram, setInstagram] = useState("");
  const [youtube, setYoutube] = useState("");

  const [hashtags, setHashtags] = useState("");
  const [googleMapCode, setGoogleMapCode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [formattedAddress, setFormattedAddress] = useState("");

  const [featured, setFeatured] = useState<any>("no");
  const [promoted, setPromoted] = useState<any>("no");
  const [expo, setExpo] = useState(0);
  const [cityguideStatus, setCityGuideStatus] = useState(1);

  const [description, setDescription] = useState("");
  const [pricing, setPricing] = useState<any[]>([]);

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const cityGuideRoutes = "/data/city/guide";

  const resetNotices = () => {
    setError(false);
    setSuccess(false);
    setErrorMessage("");
  };

  const clearMessages = () => {
    if (error || success) resetNotices();
  };

  const combinedTags = useMemo(
    () =>
      [tags, hashtags]
        .filter(Boolean)
        .map((t: any) => t.trim())
        .filter((t: any) => t.length > 0)
        .join(", "),
    [tags, hashtags]
  );

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);

    setThumbnail(defaultImage);
    setThumbnailId("img_cdn_id");

    setTitle("");
    setVenue("");
    setVenueCapacity("");
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");

    setEmail("");
    setPayLink(null);
    setPhoneNumber("");

    setWhatsapp("");
    setFacebook("");
    setTwitter("");
    setInstagram("");
    setYoutube("");

    setHashtags("");
    setGoogleMapCode("");
    setLatitude("");
    setLongitude("");
    setFormattedAddress("");

    setFeatured("no");
    setPromoted("no");
    setExpo(0);
    setCityGuideStatus(1);

    setDescription("");
    setPricing([]);
  };

  const respond = (isError: any, msg: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg || "");
  };

  const addOneCityGuideHandler = async (e: any) => {
    e.preventDefault();
    resetNotices();
    setLoading(true);

    if (!thumbnail || !thumbnailId) return respond(true, "Image required.");
    if (!title?.trim()) return respond(true, "Title is required.");
    if (!venue?.trim()) return respond(true, "Venue is required.");
    if (!startDate || !endDate || !startTime || !endTime)
      return respond(true, "Start/End date and time are required.");

    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      country_id,
      first_level_id: first_level_id || 0,
      second_level_id: second_level_id || 0,
      third_level_id: third_level_id || 0,
      fourth_level_id: fourth_level_id || 0,

      title: title?.trim(),
      slug: slugify(title),

      description: description || "",
      venue: venue || "",
      venue_capacity: venueCapacity ? Number(venueCapacity) : 0,

      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,

      thumbnail: thumbnail || defaultImage,
      image_cdn_id: thumbnailId || "",

      phone_number: phoneNumber || "",
      email_address: email || "",
      pay_link: payLink,

      whatsapp: whatsapp || "none",
      facebook: facebook || "none",
      twitter: twitter || "none",
      instagram: instagram || "none",
      youtube: youtube || "none",

      hashtag: combinedTags || "none",
      audio_adv: "none",

      google_map_code: googleMapCode || "none",
      latitude: latitude || "none",
      longitude: longitude || "none",
      formatted_address: formattedAddress || "",

      country_short: "none",
      status: cityguideStatus,
      featured,
      promoted,
      expo,

      pricing,
    };

    const { status, message } = await createOneContent({
      data,
      content: _TYPES_.CITYGUIDE,
    });

    if (status === 200 || status === 201) {
      cleanFields();
      respond(false, message);
    } else {
      respond(true, message);
    }
  };

  return (
    <AddPageShell
      backRoute={cityGuideRoutes}
      eyebrow="DoSee Data • City Guide"
      title="Add City Guide"
      subtitle={`Add a curated city guide entry for ${entry_label || "this destination"}.`}
      icon={<FiMap className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Add City Guide"
      onSubmit={addOneCityGuideHandler}
      onSubmitClick={resetNotices}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={resetNotices}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="A scenic shot for the city guide hero."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.CITY_GUIDE)}
        />
      </AddSection>

      <AddSection title="City Guide Details" hint="Title, venue and address.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              required: true,
              placeholder: "e.g., Weekend Food Festival",
              value: title,
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "venue",
              title: "Venue",
              type: "text",
              required: true,
              placeholder: "e.g., Central Park Arena",
              value: venue,
              onChange: (e: any) => {
                setVenue(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "venue_capacity",
              title: "Venue Capacity",
              type: "number",
              placeholder: "e.g., 2000",
              value: venueCapacity,
              onChange: (e: any) => {
                setVenueCapacity(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "formatted_address",
              title: "Formatted Address",
              type: "address",
              placeholder: "123 Main St, City",
              value: formattedAddress,
              onChange: (e: any) => {
                setFormattedAddress(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Date & Time"
        hint="When this guide event takes place."
        icon={<FiClock className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <InputField
            props={{
              id: "start_date",
              title: "Start Date",
              type: "date",
              required: true,
              value: startDate,
              onChange: (e: any) => {
                setStartDate(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "end_date",
              title: "End Date",
              type: "date",
              required: true,
              value: endDate,
              onChange: (e: any) => {
                setEndDate(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "start_time",
              title: "Start Time",
              type: "time",
              required: true,
              value: startTime,
              onChange: (e: any) => {
                setStartTime(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "end_time",
              title: "End Time",
              type: "time",
              required: true,
              value: endTime,
              onChange: (e: any) => {
                setEndTime(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Optional pricing tiers."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <Pricing pricing={pricing} setPricing={setPricing} />
      </AddSection>

      <ContactSection
        phone={phoneNumber}
        setPhone={setPhoneNumber}
        email={email}
        setEmail={setEmail}
        payLink={payLink}
        setPayLink={setPayLink}
        clearMessages={clearMessages}
      />

      <SocialSection
        whatsapp={whatsapp}
        setWhatsapp={setWhatsapp}
        facebook={facebook}
        setFacebook={setFacebook}
        twitter={twitter}
        setTwitter={setTwitter}
        instagram={instagram}
        setInstagram={setInstagram}
        youtube={youtube}
        setYoutube={setYoutube}
        clearMessages={clearMessages}
      />

      <GeoSection
        googleMapCode={googleMapCode}
        setGoogleMapCode={setGoogleMapCode}
        latitude={latitude}
        setLatitude={setLatitude}
        longitude={longitude}
        setLongitude={setLongitude}
        clearMessages={clearMessages}
      />

      <VisibilitySection
        statusId="cityguide_status"
        setStatus={setCityGuideStatus}
        setFeatured={setFeatured}
        setPromoted={setPromoted}
        setExpo={setExpo}
        clearMessages={clearMessages}
      />

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Add;
